import React, { useEffect, useMemo, useState } from 'react';
import { getOnboardingProgress, updateOnboardingProgress } from '../services/api';
import { OnboardingProgress, OnboardingStep } from '../types';

const STEP_HINTS: Record<string, string> = {
  verify_email: 'Confirm the admin email so password resets, invites, and alerts reach the right person.',
  add_site: 'Create the first site or facility so devices can be grouped by location.',
  add_device: 'Register at least one direct sensor or gateway-backed virtual device.',
  live_data: 'Stream telemetry in the last 24 hours so dashboards and alerts have live context.',
  create_dashboard: 'Create a custom dashboard for the first monitoring view.',
  create_alert: 'Add a threshold alert for the operating condition that matters most.',
  invite_member: 'Invite a teammate or keep a pending invite for implementation handoff.',
  branding: 'Upload a logo or platform name for white-label workspaces and reports.',
};

type OnboardingChecklistProps = {
  variant?: 'full' | 'compact';
};

export default function OnboardingChecklist({ variant = 'full' }: OnboardingChecklistProps) {
  const [progress, setProgress] = useState<OnboardingProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionKey, setActionKey] = useState('');
  const [error, setError] = useState('');
  const [compactOpen, setCompactOpen] = useState(false);
  const compact = variant === 'compact';

  useEffect(() => {
    loadProgress();
  }, []);

  const percent = useMemo(() => {
    if (!progress || progress.total === 0) return 0;
    return Math.round((progress.completed / progress.total) * 100);
  }, [progress]);

  async function loadProgress() {
    setLoading(true);
    setError('');
    try {
      const next = await getOnboardingProgress();
      setProgress(next);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to load onboarding progress.');
    } finally {
      setLoading(false);
    }
  }

  async function updateChecklist(payload: { dismissed?: boolean; overrides?: Record<string, boolean> }, key = 'checklist') {
    setActionKey(key);
    setError('');
    try {
      await updateOnboardingProgress(payload);
      await loadProgress();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to update onboarding progress.');
    } finally {
      setActionKey('');
    }
  }

  if (loading) {
    if (compact) {
      return (
        <article className="sd-panel sd-checklist-panel is-compact">
          <div className="sd-checklist-compact-static">
            <span className="sd-checklist-eyebrow">Setup Companion</span>
            <strong>Checking readiness...</strong>
          </div>
        </article>
      );
    }

    return (
      <article className="sd-panel sd-checklist-panel">
        <div className="sd-panel-header">
          <div>
            <h2>Workspace Checklist</h2>
            <p>Checking setup progress...</p>
          </div>
        </div>
      </article>
    );
  }

  if (!progress) {
    return (
      <article className={`sd-panel sd-checklist-panel ${compact ? 'is-compact' : ''}`}>
        <div className="sd-inline-alert is-danger">{error || 'Onboarding progress is unavailable.'}</div>
      </article>
    );
  }

  if (progress.dismissed) {
    if (compact) {
      return (
        <article className="sd-panel sd-checklist-panel is-compact is-dismissed">
          <div className="sd-checklist-compact-static">
            <span className="sd-checklist-eyebrow">Readiness</span>
            <strong>{progress.completed}/{progress.total} completed</strong>
          </div>
          <button
            className="sd-secondary-button"
            disabled={actionKey === 'restore'}
            onClick={() => updateChecklist({ dismissed: false }, 'restore')}
            type="button"
          >
            {actionKey === 'restore' ? 'Restoring...' : 'Restore'}
          </button>
        </article>
      );
    }

    return (
      <article className="sd-panel sd-checklist-panel is-dismissed">
        <div>
          <span className="sd-checklist-eyebrow">Workspace Checklist</span>
          <strong>{progress.completed}/{progress.total} completed</strong>
          <p>The checklist is hidden for this tenant. Restore it when implementation work resumes.</p>
        </div>
        <button
          className="sd-secondary-button"
          disabled={actionKey === 'restore'}
          onClick={() => updateChecklist({ dismissed: false }, 'restore')}
          type="button"
        >
          {actionKey === 'restore' ? 'Restoring...' : 'Restore Checklist'}
        </button>
      </article>
    );
  }

  if (compact) {
    const orderedSteps = [...progress.steps].sort((a, b) => Number(a.done) - Number(b.done));
    const visibleSteps = compactOpen ? orderedSteps : orderedSteps.slice(0, 3);

    return (
      <article className="sd-panel sd-checklist-panel is-compact">
        <button
          className="sd-checklist-compact-toggle"
          aria-expanded={compactOpen}
          onClick={() => setCompactOpen((value) => !value)}
          type="button"
        >
          <span>
            <span className="sd-checklist-eyebrow">Setup Companion</span>
            <strong>Readiness</strong>
          </span>
          <span className="sd-checklist-compact-score">
            <b>{progress.completed}/{progress.total}</b>
            <i aria-hidden="true" />
          </span>
        </button>

        <div className="sd-checklist-progress" aria-hidden="true">
          <span style={{ width: `${percent}%` }} />
        </div>

        {error && <div className="sd-inline-alert is-danger">{error}</div>}

        <div className="sd-checklist-compact-list">
          {visibleSteps.map((step) => (
            <CompactChecklistStep
              key={step.key}
              onManualDone={() => updateChecklist({ overrides: { [step.key]: true } }, step.key)}
              pending={actionKey === step.key}
              step={step}
            />
          ))}
        </div>

        <div className="sd-checklist-compact-actions">
          <button onClick={() => setCompactOpen((value) => !value)} type="button">
            {compactOpen ? 'Show less' : 'View all checklist'}
          </button>
          <button disabled={actionKey === 'refresh'} onClick={() => loadProgress()} type="button">
            {actionKey === 'refresh' ? 'Refreshing...' : 'Refresh'}
          </button>
          {compactOpen && (
            <button
              disabled={actionKey === 'dismiss'}
              onClick={() => updateChecklist({ dismissed: true }, 'dismiss')}
              type="button"
            >
              {actionKey === 'dismiss' ? 'Hiding...' : 'Hide'}
            </button>
          )}
        </div>
      </article>
    );
  }

  return (
    <article className="sd-panel sd-checklist-panel">
      <div className="sd-panel-header">
        <div>
          <span className="sd-checklist-eyebrow">Workspace Checklist</span>
          <h2>Finish Tenant Setup</h2>
          <p>Progress is calculated from live tenant data. Manual marks are available when a task was handled outside the platform.</p>
        </div>
        <div className="sd-checklist-score">
          <strong>{percent}%</strong>
          <span>{progress.completed}/{progress.total} complete</span>
        </div>
      </div>

      <div className="sd-checklist-progress" aria-hidden="true">
        <span style={{ width: `${percent}%` }} />
      </div>

      {error && <div className="sd-inline-alert is-danger">{error}</div>}

      <div className="sd-checklist-grid">
        {progress.steps.map((step) => (
          <ChecklistStep
            key={step.key}
            onManualDone={() => updateChecklist({ overrides: { [step.key]: true } }, step.key)}
            pending={actionKey === step.key}
            step={step}
          />
        ))}
      </div>

      <div className="sd-checklist-actions">
        <button
          className="sd-secondary-button"
          disabled={actionKey === 'refresh'}
          onClick={() => loadProgress()}
          type="button"
        >
          Refresh Progress
        </button>
        <button
          className="sd-secondary-button"
          disabled={actionKey === 'dismiss'}
          onClick={() => updateChecklist({ dismissed: true }, 'dismiss')}
          type="button"
        >
          Hide Checklist
        </button>
      </div>
    </article>
  );
}

function CompactChecklistStep({
  onManualDone,
  pending,
  step,
}: {
  onManualDone: () => void;
  pending: boolean;
  step: OnboardingStep;
}) {
  return (
    <div className={`sd-checklist-compact-step ${step.done ? 'is-done' : ''}`}>
      <span className="sd-checklist-step-icon">{step.done ? 'OK' : '--'}</span>
      <div>
        <strong>{step.label}</strong>
        <small>{step.done ? 'Complete' : STEP_HINTS[step.key] || 'Pending setup task'}</small>
      </div>
      {!step.done && (
        <button disabled={pending} onClick={onManualDone} type="button">
          {pending ? 'Saving...' : 'Mark'}
        </button>
      )}
    </div>
  );
}

function ChecklistStep({
  onManualDone,
  pending,
  step,
}: {
  onManualDone: () => void;
  pending: boolean;
  step: OnboardingStep;
}) {
  return (
    <div className={`sd-checklist-step ${step.done ? 'is-done' : ''}`}>
      <span className="sd-checklist-step-icon">{step.done ? 'OK' : '--'}</span>
      <div>
        <strong>{step.label}</strong>
        <p>{STEP_HINTS[step.key] || 'Complete this setup step to improve tenant readiness.'}</p>
        <small>{step.auto ? 'Auto-computed' : 'Marked manually'}</small>
      </div>
      {!step.done && (
        <button disabled={pending} onClick={onManualDone} type="button">
          {pending ? 'Saving...' : 'Mark Done'}
        </button>
      )}
    </div>
  );
}
